import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import type { MarketingActivity, ActivityType } from '@/types';
import { cn } from '@/lib/utils';
import { activityTypeBg } from '@/lib/tag-palette';
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { de } from 'date-fns/locale';

const TYPE_COLORS: Record<ActivityType, string> = {
  google_ads: activityTypeBg('google_ads'),
  linkedin_ads: activityTypeBg('linkedin_ads'),
  print: activityTypeBg('print'),
  email: activityTypeBg('email'),
  campaign: activityTypeBg('campaign'),
  social: activityTypeBg('social'),
  event: activityTypeBg('event'),
  content: activityTypeBg('content'),
  seo: activityTypeBg('seo'),
  webinar: activityTypeBg('webinar'),
  other: activityTypeBg('other'),
};

interface CalendarViewProps {
  activities: MarketingActivity[];
  loading: boolean;
  onActivityClick: (id: string) => void;
  onCreateAtSlot?: (date: string, time: string) => void;
}

export default function CalendarView({
  activities,
  loading,
  onActivityClick,
  onCreateAtSlot,
}: CalendarViewProps) {
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month' | 'year'>('month');

  const { weeks } = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const startDay = start.getDay();
    const padStart = startDay === 0 ? 6 : startDay - 1;
    const firstCell = addDays(start, -padStart);
    const weeks: Date[][] = [];
    let d = firstCell;
    while (d <= end || weeks.length < 6) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(d);
        d = addDays(d, 1);
      }
      weeks.push(week);
      if (weeks.length >= 6 && !isSameMonth(d, currentMonth)) break;
    }
    return { weeks };
  }, [currentMonth]);

  const activitiesByDay = useMemo(() => {
    const map = new Map<string, MarketingActivity[]>();
    for (const a of activities) {
      const start = a.start_date ? parseISO(a.start_date) : null;
      const end = a.end_date ? parseISO(a.end_date) : null;
      if (!start) continue;
      const from = new Date(start);
      const to = end ? new Date(end) : new Date(from);
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const key = format(d, 'yyyy-MM-dd');
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(a);
      }
    }
    return map;
  }, [activities]);

  const weekDays = useMemo(
    () =>
      [1, 2, 3, 4, 5, 6, 0].map((d) =>
        format(new Date(2024, 0, d), 'EEE', { locale: de })
      ),
    []
  );
  const weekRange = useMemo(() => {
    const start = startOfWeek(currentMonth, { weekStartsOn: 1 });
    const end = endOfWeek(currentMonth, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);
  const dayKey = format(currentMonth, 'yyyy-MM-dd');
  const dayActivities = activitiesByDay.get(dayKey) ?? [];
  const yearMonths = useMemo(
    () => Array.from({ length: 12 }, (_, index) => new Date(currentMonth.getFullYear(), index, 1)),
    [currentMonth]
  );
  const hourSlots = useMemo(() => Array.from({ length: 16 }, (_, index) => index + 6), []);

  const getHeaderLabel = () => {
    if (viewMode === 'day') {
      const start = weekRange[0]!;
      const end = weekRange[6]!;
      return `${format(start, 'd. MMM', { locale: de })} - ${format(end, 'd. MMM yyyy', { locale: de })} (Fokus: ${format(currentMonth, 'EEE d. MMM', { locale: de })})`;
    }
    if (viewMode === 'week') {
      const start = weekRange[0]!;
      const end = weekRange[6]!;
      return `${format(start, 'd. MMM', { locale: de })} - ${format(end, 'd. MMM yyyy', { locale: de })}`;
    }
    if (viewMode === 'year') return format(currentMonth, 'yyyy', { locale: de });
    return format(currentMonth, 'MMMM yyyy', { locale: de });
  };

  const renderDayCell = (day: Date, opts?: { highlightFocusedDay?: boolean }) => {
    const key = format(day, 'yyyy-MM-dd');
    const activitiesForDay = activitiesByDay.get(key) ?? [];
    const isCurrentMonth = isSameMonth(day, currentMonth);
    const isFocusedDay = key === dayKey;
    return (
      <div
        key={key}
        className={cn(
          'min-h-[80px] cursor-pointer rounded-lg border border-border p-1',
          !isCurrentMonth && 'bg-background dark:bg-card/30',
          opts?.highlightFocusedDay && isFocusedDay && 'ring-2 ring-border'
        )}
        onClick={() => setCurrentMonth(day)}
      >
        <span
          className={cn(
            'text-sm',
            !isCurrentMonth && 'text-muted',
            opts?.highlightFocusedDay && isFocusedDay && 'font-semibold text-foreground dark:text-muted'
          )}
        >
          {format(day, 'd')}
        </span>
        <div className="mt-1 space-y-0.5">
          {activitiesForDay.slice(0, 3).map((a) => (
            <button
              key={a.id}
              type="button"
              className={cn(
                'block w-full truncate rounded px-1 py-0.5 text-left text-xs text-white hover:opacity-90',
                TYPE_COLORS[a.type] ?? TYPE_COLORS.other
              )}
              title={a.title}
              onClick={(e) => {
                e.stopPropagation();
                onActivityClick(a.id);
              }}
            >
              {a.title}
            </button>
          ))}
          {activitiesForDay.length > 3 && (
            <span className="text-xs text-muted">
              +{activitiesForDay.length - 3} weitere
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderWeekTimeGrid = (opts?: { highlightFocusedDay?: boolean }) => (
    <div className="overflow-auto rounded-lg border border-border">
      <div className="grid min-w-[860px] grid-cols-[72px_repeat(7,minmax(110px,1fr))]">
        <div className="sticky left-0 z-20 border-b border-r border-border bg-card p-2 text-xs font-medium text-muted dark:bg-background">
          Zeit
        </div>
        {weekRange.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const isFocused = opts?.highlightFocusedDay && isSameDay(day, currentMonth);
          const dayItems = activitiesByDay.get(key) ?? [];
          return (
            <div
              key={`header-${key}`}
              className={cn(
                'border-b border-r border-border p-2 text-center',
                isFocused && 'bg-card-hover/80 dark:bg-primary-hover/20'
              )}
            >
              <p
                className={cn(
                  'text-xs text-muted',
                  isFocused && 'text-foreground dark:text-muted'
                )}
              >
                {format(day, 'EEE', { locale: de })}
              </p>
              <p
                className={cn(
                  'text-sm font-semibold',
                  isFocused && 'text-foreground dark:text-muted'
                )}
              >
                {format(day, 'd.MM.')}
              </p>
              <div className="mt-1 space-y-1">
                {dayItems.slice(0, 1).map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={cn(
                      'w-full truncate rounded px-1 py-0.5 text-left text-[10px] text-white hover:opacity-90',
                      TYPE_COLORS[a.type] ?? TYPE_COLORS.other
                    )}
                    onClick={() => onActivityClick(a.id)}
                    title={a.title}
                  >
                    {a.title}
                  </button>
                ))}
                {dayItems.length > 1 && (
                  <p className="text-[10px] text-muted">+{dayItems.length - 1} weitere</p>
                )}
              </div>
            </div>
          );
        })}

        {hourSlots.map((hour) => (
          <div key={`row-${hour}`} className="contents">
            <div className="sticky left-0 z-10 h-14 border-b border-r border-border bg-card px-2 py-1 text-xs text-muted dark:bg-background">
              {String(hour).padStart(2, '0')}:00
            </div>
            {weekRange.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const isFocused = opts?.highlightFocusedDay && isSameDay(day, currentMonth);
              return (
                <button
                  key={`slot-${key}-${hour}`}
                  type="button"
                  className={cn(
                    'h-14 border-b border-r border-border text-left transition hover:bg-background dark:hover:bg-card-hover/40',
                    isFocused && 'bg-card-hover/40 dark:bg-primary-hover/10'
                  )}
                  title={`${format(day, 'dd.MM.yyyy')} ${String(hour).padStart(2, '0')}:00`}
                  onClick={() => onCreateAtSlot?.(key, `${String(hour).padStart(2, '0')}:00`)}
                >
                  <span className="sr-only">Termin anlegen</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 35 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square animate-pulse rounded-lg bg-card-hover"
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              {getHeaderLabel()}
            </h2>
            <p className="text-xs text-muted">Ansicht: {viewMode === 'day' ? 'Tag' : viewMode === 'week' ? 'Woche' : viewMode === 'month' ? 'Monat' : 'Jahr'}</p>
          </div>
          <div className="flex gap-2">
            <div className="mr-2 flex overflow-hidden rounded-md border border-border">
              {(['day', 'week', 'month', 'year'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={cn(
                    'px-2 py-1 text-xs',
                    viewMode === mode ? 'bg-card-hover text-foreground dark:bg-primary-hover/30 dark:text-muted' : 'hover:bg-background dark:hover:bg-card-hover'
                  )}
                  onClick={() => setViewMode(mode)}
                  title={`Auf ${mode === 'day' ? 'Tag' : mode === 'week' ? 'Woche' : mode === 'month' ? 'Monat' : 'Jahr'} filtern`}
                >
                  {mode === 'day' ? 'Tag' : mode === 'week' ? 'Woche' : mode === 'month' ? 'Monat' : 'Jahr'}
                </button>
              ))}
            </div>
            <button
              type="button"
              title={`Vorherige ${viewMode === 'day' ? 'Tag' : viewMode === 'week' ? 'Woche' : viewMode === 'month' ? 'Monat' : 'Jahr'}-Ansicht`}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-background dark:hover:bg-card-hover"
              onClick={() =>
                setCurrentMonth((m) =>
                  viewMode === 'day'
                    ? addDays(m, -1)
                    : viewMode === 'week'
                      ? addDays(m, -7)
                      : viewMode === 'month'
                        ? new Date(m.getFullYear(), m.getMonth() - 1)
                        : new Date(m.getFullYear() - 1, m.getMonth(), m.getDate())
                )
              }
            >
              Zurück
            </button>
            <button
              type="button"
              title={`Nächste ${viewMode === 'day' ? 'Tag' : viewMode === 'week' ? 'Woche' : viewMode === 'month' ? 'Monat' : 'Jahr'}-Ansicht`}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-background dark:hover:bg-card-hover"
              onClick={() =>
                setCurrentMonth((m) =>
                  viewMode === 'day'
                    ? addDays(m, 1)
                    : viewMode === 'week'
                      ? addDays(m, 7)
                      : viewMode === 'month'
                        ? new Date(m.getFullYear(), m.getMonth() + 1)
                        : new Date(m.getFullYear() + 1, m.getMonth(), m.getDate())
                )
              }
            >
              Weiter
            </button>
          </div>
        </div>

        {viewMode === 'day' && (
          <div className="space-y-3">
            {renderWeekTimeGrid({ highlightFocusedDay: true })}
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 text-xs text-muted">
                Fokus: {format(currentMonth, 'EEEE, d. MMMM yyyy', { locale: de })}
              </p>
              {dayActivities.length === 0 ? (
                <p className="text-sm text-muted">Keine Aktivitäten am ausgewählten Tag.</p>
              ) : (
                <div className="space-y-2">
                  {dayActivities.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className={cn(
                        'block w-full rounded-md px-3 py-2 text-left text-sm text-white hover:opacity-90',
                        TYPE_COLORS[a.type] ?? TYPE_COLORS.other
                      )}
                      onClick={() => onActivityClick(a.id)}
                    >
                      {a.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === 'week' && (
          renderWeekTimeGrid()
        )}

        {viewMode === 'month' && (
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day) => (
              <div
                key={day}
                className="py-1 text-center text-xs font-medium text-muted"
              >
                {day}
              </div>
            ))}
            {weeks.flatMap((week) => week.map((day) => renderDayCell(day)))}
          </div>
        )}

        {viewMode === 'year' && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {yearMonths.map((month) => {
              const monthStart = startOfMonth(month);
              const monthEnd = endOfMonth(month);
              const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
              const uniqueIds = new Set<string>();
              for (const day of days) {
                const dayItems = activitiesByDay.get(format(day, 'yyyy-MM-dd')) ?? [];
                for (const item of dayItems) uniqueIds.add(item.id);
              }
              return (
                <button
                  key={month.toISOString()}
                  type="button"
                  className="rounded-lg border border-border p-3 text-left hover:bg-background dark:hover:bg-card-hover/50"
                  onClick={() => {
                    setCurrentMonth(month);
                    setViewMode('month');
                  }}
                >
                  <p className="text-sm font-semibold">{format(month, 'MMMM', { locale: de })}</p>
                  <p className="text-xs text-muted">{uniqueIds.size} Aktivitäten</p>
                </button>
              );
            })}
          </div>
        )}

        {activities.length === 0 && (
          <p className="mt-4 text-center text-sm text-muted">
            Keine Aktivitäten in diesem Zeitraum.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
